import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';

import { FormSectionCollapsible, Button, Label, Separator, DefaultTooltip, ProgressMultiItem, ProgressMulti, Progress as ProgressComponent, DefaultSheet, SheetBody } from 'twyne-new.ui';

import { LOADING } from '@Stores/BaseStore';

import { Loader } from './card';

const propTypes = {
  header: PropTypes.string,
  field: PropTypes.string,
  labelFormatting: PropTypes.func,
  data: PropTypes.any,
  columns: PropTypes.array,
  limit: PropTypes.number,
  color: PropTypes.string,
  addFilter: PropTypes.func,
  needApply: PropTypes.bool,
  filtersApplied: PropTypes.bool,
  labeled: PropTypes.func,
  handleApplyFilters: PropTypes.func,
};

const formatNumber = value => <NumberFormat value={value} displayType="text" thousandSeparator="," />
const formatPercent = value => `(${(value * 100).toFixed(2)}%)`

function Progress({ max, color, isCompleted, value, valuePC }) {
  return <>
    <ProgressMultiItem
      color={color}
      value={value / max * 100}
      tiptap={`${!isCompleted && 'Not '}Completed: ${value} ${formatPercent(valuePC)}`}
      className='cursor-pointer'
    />
  </>
}

function ProgressHeader({ data, index, labeled, field, labelFormatting }) {
  const id = `${field}-${index}`

  return <>
    {data.name ? <DefaultTooltip content={`ID: ${data.key}`}>
      <span id={id} className="cursor-pointer" >
        {labeled ? labeled(data, id, index, field) : <>
          {labelFormatting(field, data.name, data.key)}
        </>}
      </span>
    </DefaultTooltip> : <span>{data.key || "--"}</span>}
    <span className="float-right">{formatNumber(data.total)} {formatPercent(data.totalPC)}</span>
  </>
}

function ProgressContent({ data, total, field, labelFormatting, labeled, onAddFilter }) {
  return data.map((bucket, idx) => <div key={`${bucket.name}-${idx}`} className='mb-2'>
    <ProgressHeader data={bucket} field={field} index={idx} labelFormatting={labelFormatting} labeled={labeled} />
    <ProgressMulti fadeColor className="cursor-pointer" onClick={() => onAddFilter(bucket.key)}>
      <Progress max={total} value={bucket.completed ?? bucket.total} isCompleted valuePC={bucket.completedPC ?? bucket.totalPC} index={idx} field={field} />
      {bucket.notCompleted !== null && <Progress color="success" max={total} value={bucket.notCompleted} valuePC={bucket.notCompletedPC} index={idx} field={field} />}
    </ProgressMulti>
  </div>)
}

function Others({ data, total, limit, field, columns, header, onAddFilter, labelFormatting, labeled }) {
  const [totalOthers, setTotalOthers] = useState(0)
  const [percentSum, setPercentSum] = useState(0)

  useEffect(() => {
    const list = data.slice(limit)
    let totalOthers = 0

    for (const bucket of list) {
      totalOthers += bucket.completed + bucket.notCompleted
    }

    setTotalOthers(totalOthers)
    setPercentSum(totalOthers / total)
  }, [total, data, limit])

  if (!limit || data.length <= limit) return null

  return <>
    <div className='flex justify-between items-center'>
      <Label>Others imbig</Label>
      <Label>{formatNumber(totalOthers)} {formatPercent(percentSum)}</Label>
    </div>
    <ProgressComponent className='h-4' value={totalOthers} min="0" max={total} />
    <DefaultSheet
      title={header}
      triggerProps={{ asChild: true }}
      trigger={<div className="text-center">
        <Button className="mt-2" size="sm" color="accent" variant='light'>show all</Button>
      </div>}
    >
      <SheetBody>
        <div className='flex justify-between items-center mb-2'>
          <Label>{columns[0]}</Label>
          <Label>{columns[1]}</Label>
        </div>
        <ProgressContent data={data} field={field} onAddFilter={onAddFilter} labelFormatting={labelFormatting} labeled={labeled} total={total} />
      </SheetBody>
    </DefaultSheet>
  </>
}

const Header = ({ header, isRenderAplly, onApplyFilters }) => <div className='flex justify-between items-center'>
  {header}
  {isRenderAplly && <Button
    type="buttom"
    color="accent"
    size="sm"
    variant='ghost'
    onClick={e => {
      e.stopPropagation()
      onApplyFilters()
    }}
  >
    Apply
  </Button>}
</div>

export default function DetailsCard({ onApplyFilters, filtersApplied, status, limit, data, filters, header, columns, onAddFilter, field, labeled, labelFormatting, total }) {
  const isRenderAplly = filters[field] && !filtersApplied

  const dataLimit = limit ? data && data.slice(0, limit) : data

  return <div className='break-inside-avoid mb-4'>
    <Loader loading={status === LOADING || !data}>
      <FormSectionCollapsible
        defaultOpen
        border
        triggerClass='py-2 px-4 twy-subtle-neutral'
        contentClass='p-4 overflow-visible'
        titleClass='text-base pr-2'
        sectionClass='w-full overflow-visible'
        title={<Header header={header} isRenderAplly={isRenderAplly} onApplyFilters={onApplyFilters} />}
      >
        <div className='flex justify-between items-center'>
          <Label>{columns[0]}</Label>
          <Label>{columns[1]}</Label>
        </div>
        <Separator className='my-2' />
        <ProgressContent data={dataLimit} field={field} onAddFilter={onAddFilter} labelFormatting={labelFormatting} labeled={labeled} total={total} />
        <Others data={data} limit={limit} total={total} field={field} columns={columns} header={header} onAddFilter={onAddFilter} labelFormatting={labelFormatting} labeled={labeled} />
      </FormSectionCollapsible>
    </Loader>
  </div>
}

DetailsCard.propTypes = propTypes;
